import React, { useEffect, useState } from 'lib/react';
import { useDispatch, useSelector } from 'lib/redux';
import {
  EVButton,
  EVDialogTitle,
  EVDialogContentText,
  EVDialogContent,
  EVDialogActions,
  EVDialog,
  EVClear,
} from 'lib/components';
import OrderHistoryStyles from 'features/orderHistory/OrderHistoryStyles';
import { useTranslation } from 'lib/translation';
import useMediaQuery from 'hooks/useMediaQuery';
import ReportsList from 'components/orders-list/ReportsList.component';
import ReportsTable from 'components/orders-list/ReportsTable.component';
import ReportsListSubscription from 'components/orders-list/ReportsListSubscription.component';
import ReportsTableSubscription from 'components/orders-list/ReportsTableSubscription.component';
import { getOrderHistoryCells, getSubscriptionOrderHistoryCells } from 'features/orderHistory/OrderHistory.utils';
import { CLOSE_DUPLICATE_DIALOG } from 'constants/ActionTypes';
import ExpandedClaimInfoSection from 'features/orderHistory/components/ExpandedClaimInfoSection.component';
import {
  isOrderingDisabledSelector,
  isSubscriptionDashboardV2EnabledSelector,
} from 'utils/appSelectors.utils';
import DuplicateReportsStyles from './DuplicateReportsStyles';
import { getDuplicateReportsColumns } from '../Order.utils';

function DuplicateReportsDialog() {
  const {
    duplicateReportsData, duplicateReportsDialogOpen, availableActions, loading, subscriptionDetailsMap,
  } = useSelector(
    ({
      order, orderHistory,
    }) => ({
      ...order,
      availableActions: orderHistory.availableActions,
      subscriptionDetailsMap: orderHistory.subscriptionDetailsMap,
    }),
  );

  const classes = DuplicateReportsStyles();
  const [open, setOpen] = useState(false);
  const [expandedRowFlags, setExpandedRowFlags] = useState({});
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const orderHistoryClasses = OrderHistoryStyles();

  const isMobile = useMediaQuery(640);
  const isTablet = useMediaQuery(1200);
  const isOrderingDisabled = useSelector(isOrderingDisabledSelector);
  const isSubscriptionDashboardV2Enabled = useSelector(isSubscriptionDashboardV2EnabledSelector);

  // #genai - nested ternaries extracted into named values; same component selection as before
  const useListLayout = isMobile || isTablet;
  const subscriptionReportsComponent = useListLayout ? ReportsListSubscription : ReportsTableSubscription;
  const standardReportsComponent = useListLayout ? ReportsList : ReportsTable;
  const ReportsComponent = isSubscriptionDashboardV2Enabled ? subscriptionReportsComponent : standardReportsComponent;

  const onClose = () => {
    setOpen(false);
    dispatch({ type: CLOSE_DUPLICATE_DIALOG });
  };

  useEffect(() => {
    if (duplicateReportsData?.length > 0) {
      setOpen(duplicateReportsDialogOpen);
    }
  }, [duplicateReportsDialogOpen, duplicateReportsData]);

  const handleExpandedRowVisibility = (key) => setExpandedRowFlags((prevState) => ({ ...prevState, [key]: !prevState[key] }));

  return (
    <EVDialog
      fullWidth
      maxWidth="xl"
      open={open}
      onClose={onClose}
    >
      <EVDialogTitle className={classes.title}>
        <b>{t('duplicate_reports__existing_orders_text')}</b>
        <EVClear onClick={onClose} className={classes.closeButton} />
      </EVDialogTitle>
      <EVDialogContent className={classes.dialogContentContainer}>
        <EVDialogContentText className={classes.evDialogContentText}>
          {t('duplicate_reports__review_previous_orders')}
        </EVDialogContentText>
        <ReportsComponent
          fields={!isSubscriptionDashboardV2Enabled && getDuplicateReportsColumns(t, classes)}
          reportDetailsList={isSubscriptionDashboardV2Enabled
            ? getSubscriptionOrderHistoryCells(
              duplicateReportsData || [],
              t,
              orderHistoryClasses,
              availableActions,
              isOrderingDisabled,
              subscriptionDetailsMap || {},
            )
            : getOrderHistoryCells(
              duplicateReportsData || [],
              t,
              orderHistoryClasses,
              availableActions,
              isOrderingDisabled,
            )}
          loading={loading}
          ExpandedRowComponent={ExpandedClaimInfoSection}
          expandedRowFlags={expandedRowFlags}
          onExpandedRowFlagsChange={handleExpandedRowVisibility}
        />
      </EVDialogContent>
      <EVDialogActions>
        <EVButton style={{ textTransform: 'none' }} onClick={onClose} variant="contained" className={classes.button}>
          {t('duplicate_reports__continue_order')}
        </EVButton>
      </EVDialogActions>
    </EVDialog>
  );
}
export default DuplicateReportsDialog;
